import {startTransition, useEffect} from 'react';
import useShopperBase from 'commerceAPI/hooks/useShopper';

import {EActionType, useUI, useApplePay} from 'coreHooks';
import {EEventSource} from 'coreClasses/events/IEvents';
import {ECustomerAuthType} from 'coreClasses/customer/ICustomer';
import {IAddProductToWishlist, IAddProductToCart, IMoveProductFromBasketToWishlist} from 'coreStore/data/ui/types';
import {EAddToCartType} from 'coreStore/data/ui/types';

import {useWishlist, useAfterAction} from 'brandHooks';
import {useAddToCart} from 'brandHooks/useAddToCart';
import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

export const useShopper = (opts = {}) => {
	const useBaseShopper = useShopperBase(opts);
	const {customer, basket} = useBaseShopper;
	const wishlist = useWishlist();
	const applePayProcessor = useApplePay();
	const afterAction = useAfterAction();
	const addToCart = useAddToCart();
	const {managerRepository} = useGeneralProvider();
	const addProductToCart: IAddProductToCart = useUI('addProductToCart');
	const addProductToWishlist: IAddProductToWishlist  = useUI('addProductToWishlist');
	const moveProductFromBasketToWishlist: IMoveProductFromBasketToWishlist = useUI('moveProductFromBasketToWishlist');
	const sizeSelectorSelectedPID: string | null = useUI('sizeSelectorSelectedPID');
	const sizeSelectorSelectedValue: string | null = useUI('sizeSelectorSelectedValue');
	const isWishlistEnabledNotAuth: boolean =
		managerRepository.getConfigurationsMgr()
			.get('sitePreferences.enableNotAuthenticatedCustomerWishlist');

	/*
	* Adds product to wishlist retrieving product data stored in Redux
	* if the wishlist is not enabled for guest users and user is not logged, it opens login flyout and wait for login
	* if the user tries to add a product without choose the size, it opens size selector and wait for size
	*/
	useEffect(() => {
		if (!useBaseShopper.isFirstAuthComplete) {
			return () => null;
		}

		if (
			!isWishlistEnabledNotAuth &&
			customer.authType !== ECustomerAuthType.REGISTERED &&
			!!addProductToWishlist
		) {
			managerRepository.getFlyoutsMgr().openFlyout('login', true);
		} else if (
			(isWishlistEnabledNotAuth || customer.authType === ECustomerAuthType.REGISTERED) &&
			!!addProductToWishlist
		) {
			const {productId, variationGroupId, quantity, source, sourceSpecification}: IAddProductToWishlist = addProductToWishlist;
			const isAfterLogin = addProductToWishlist.shouldWaitToReset;
			const isNotPDP: boolean = source !== EEventSource.PDP;

			const addToWishlist = (productVariationId: string, variationGroupId: string) => {
				const dataLayerObj = {
					source,
					sourceSpecification,
				};

				wishlist
					.createListItem({
						id: productVariationId,
						c_variationGroupId: variationGroupId,
						quantity,

					}, dataLayerObj,)
					.then(() => afterAction(EActionType.WISHLIST_ADDED))
					.catch(() => afterAction(EActionType.WISHLIST_ERROR))
					.finally(() => {
						managerRepository.getUiMgr().resetProductToAddToWishlist();
						managerRepository.getUiMgr().resetSizeSelectorSelectedPid();
					});
			};

			managerRepository.getProductsMgr().getProductSync(variationGroupId).then((product) => {
				let relatedProductsInWishlist = [];

				if (!isAfterLogin) {
					if (isNotPDP && !productId) {
						if (product.hasMultipleVariationGroups()) {
							relatedProductsInWishlist.push(
								...product
									.getVariationGroupIds()
									.reduce((currentValue, id) => {
										return [...wishlist.findItemsByVariationGroupId(id), ...currentValue];
									}, [])
							);
						} else {
							relatedProductsInWishlist.push(
								...wishlist.findItemsByVariationGroupId(variationGroupId)
							);
						}
					} else {
						const existantSingleProduct = wishlist.findItemByProductId(productId);

						if (existantSingleProduct) {
							relatedProductsInWishlist.push(existantSingleProduct);
						}
					}
				} else if (sizeSelectorSelectedPID) {
					const existantSingleProduct = wishlist.findItemByProductId(sizeSelectorSelectedPID);

					if (existantSingleProduct) {
						relatedProductsInWishlist.push(existantSingleProduct);
					}
				} else {
					const existantSingleVariationGroup = wishlist.findItemsByVariationGroupId(product.getProductId());

					if (existantSingleVariationGroup) {
						relatedProductsInWishlist = existantSingleVariationGroup;
					}
				}

				const isProductInWishlist: boolean = relatedProductsInWishlist.length > 0;

				if (isProductInWishlist) {
					if (isNotPDP && !productId && !sizeSelectorSelectedPID) {
						wishlist
							.removeListItemsByVariationGroupIds([product.getProductId()])
							.then(() => afterAction(EActionType.WISHLIST_REMOVED))
							.catch(() => afterAction(EActionType.WISHLIST_ERROR))
							.finally(() => managerRepository.getUiMgr().resetProductToAddToWishlist());
					} else {
						wishlist
							.removeListItem(relatedProductsInWishlist[0].id)
							.then(() => afterAction(EActionType.WISHLIST_REMOVED))
							.catch(() => afterAction(EActionType.WISHLIST_ERROR))
							.finally(() => managerRepository.getUiMgr().resetProductToAddToWishlist());
					}
				} else {
					if (!productId) {
						if (sizeSelectorSelectedPID) {
							addToWishlist(sizeSelectorSelectedPID, variationGroupId);
						} else if (product?.isSingleVariant()) {
							addToWishlist(product.getSingleVariant().getProductId(), variationGroupId);
						} else {
							managerRepository.getUiMgr().showSizeSelector(variationGroupId);
						}
					} else {
						addToWishlist(productId, variationGroupId);
					}
				}
			});
		}
	}, [addProductToWishlist?.variationGroupId, sizeSelectorSelectedPID, customer.authType]);

	/*
	* Adds a product to cart retrieving product data stored in Redux
	* if the user tries to add a product without choose the size, it opens size selector and wait for size
	*/
	useEffect(() => {
		if (!useBaseShopper.isFirstAuthComplete) {
			return () => null;
		}

		if (addProductToCart) {
			let {productId, size} = addProductToCart;
			const {variationGroupId, quantity, type, source, parentSelector} = addProductToCart;

			if (!productId && !sizeSelectorSelectedPID) {
				managerRepository.getUiMgr().showSizeSelector
				(
					variationGroupId,
					null,
					parentSelector
				);
			} else if (sizeSelectorSelectedPID) {
				productId = sizeSelectorSelectedPID;
				size = sizeSelectorSelectedValue;
			}

			if (productId) {
				switch (type) {
					case EAddToCartType.ADD_TO_CART:
					case EAddToCartType.BUY_NOW:
					case EAddToCartType.MOVE_FROM_WISHLIST:
						// A transition: the minicart count shows the new items while the request runs.
						startTransition(async () => {
							await addToCart({productId, variationGroupId, size, quantity, type, source});
							managerRepository.getUiMgr().resetProductToAddToCart();
							managerRepository.getUiMgr().resetSizeSelectorSelectedPid();
							managerRepository.getUiMgr().setUI({sizeSelectorSelectedValue: null});
						});

						break;
					case EAddToCartType.APPLEPAY :
						if (applePayProcessor) {
							applePayProcessor.applePayHandler(productId);
							managerRepository.getUiMgr().resetProductToAddToCart();
							managerRepository.getUiMgr().resetSizeSelectorSelectedPid();
						}

						break;
				}
			}
		}
	}, [addProductToCart, sizeSelectorSelectedPID]);

	/*
	* Moves a product from cart to wishlist retrieving product data stored in Redux
	* if the wishlist is not enabled for guest users and user is not logged, it opens login flyout and wait for login
	*/
	useEffect(() => {
		if (
			!useBaseShopper.isFirstAuthComplete
			|| !basket.isReady()
		) {
			return () => null;
		}

		if (
			!isWishlistEnabledNotAuth &&
			customer.authType !== ECustomerAuthType.REGISTERED &&
			!!moveProductFromBasketToWishlist
		) {
			managerRepository.getFlyoutsMgr().openFlyout('login', true);
		} else if (
			(isWishlistEnabledNotAuth || customer.authType === ECustomerAuthType.REGISTERED) &&
			!!moveProductFromBasketToWishlist &&
			basket.customerInfo.customerId === customer.customerId
		) {
			basket
				.moveBasketItemToWishlistPartialItem(moveProductFromBasketToWishlist)
				.then(() => afterAction(EActionType.MOVED_TO_WISHLIST))
				.catch(() => afterAction(EActionType.WISHLIST_ERROR))
				.finally(() => managerRepository.getUiMgr().resetProductToMoveFromBasketToWishlist());
		}
	}, [
		moveProductFromBasketToWishlist,
		basket?.customerInfo?.customerId,
		customer.authType,
		basket.isReady()
	]);

	return {
		...useBaseShopper
	};
};
